import type { ApiError } from '@/stores/auth'
import type { ErrorOptions } from '@/utils/errorHandler'
import { getUserFriendlyMessage, handleApiError, isRetryableError, normalizeError } from '@/utils/errorHandler'
import { useCallback } from 'react'

export const useErrorHandler = () => {
	const handleError = useCallback((error: unknown, options?: ErrorOptions): ApiError => {
		return handleApiError(error, options)
	}, [])

	// Re-throws after logging, so callers can still react to the error.
	const handleAsyncError = useCallback(async <T>(promise: Promise<T>, options?: ErrorOptions): Promise<T> => {
		try {
			return await promise
		} catch (error) {
			handleApiError(error, options)
			throw error
		}
	}, [])

	const getErrorMessage = useCallback((error: unknown): string => {
		return getUserFriendlyMessage(error)
	}, [])

	const normalize = useCallback((error: unknown): ApiError => {
		return normalizeError(error)
	}, [])

	const isRetryable = useCallback((error: unknown): boolean => {
		return isRetryableError(error)
	}, [])

	return {
		handleError,
		handleAsyncError,
		getErrorMessage,
		normalize,
		isRetryable,
	}
}
